import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/tailwind-base/utils';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-lg font-bold button-case text-center no-underline transition-colors leading-extraTight ring-offset-white focus:outline focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        outline:
          'bg-background text-foreground hover:bg-accent hover:text-accent-foreground border border-input',
        ghost: 'bg-transparent text-foreground hover:bg-accent',
        cta: 'bg-cta text-cta-foreground hover:bg-cta-hover',
        link: 'border-0 text-foreground-link text-base font-semibold leading-normal underline hover:no-underline',
        nav: `text-white bg-transparent
            hover:bg-nav-button-hover
            active:bg-nav-button-active
            focus:outline-transparent
            focus-visible:bg-nav-button-focus-visible
            focus-visible:ring-2
            focus-visible:ring-offset-2 focus-visible:ring-offset-brand-dark
            `,
      },
      size: {
        default: 'min-h-11 px-4 py-2',
        auto: 'w-auto h-auto flex-1 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'min-h-11 px-8',
        icon: 'h-11 w-11 p-2',
      },
    },
  }
);

type VariantTypes =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'cta'
  | 'link'
  | 'nav';

type SizeTypes = 'default' | 'auto' | 'sm' | 'lg' | 'icon';

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  className?: string;
  hideOutline?: boolean;
  size?: SizeTypes;
  asChild?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: VariantTypes;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      size = 'default',
      asChild = false,
      type = 'button',
      variant = 'primary',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...(asChild ? {} : { type })}
        {...props}
      />
    );
  }
);
